import { View, animate } from '@play-co/timestep-core/ui';
import { SpineView, spineLoader } from '@play-co/timestep-spine';
import { DOWNSCALE_FACTOR } from 'src/conf/scale';
import { getRootView, getScreenDimensions } from 'src/lib/utils';
import uiConfig from 'src/lib/ui/config';
import StateObserver from 'src/StateObserver';
import { hideLoading, showLoading } from 'src/state/ui';

/**
 * The path to the spine-animated spinner asset
 */
const SPINNER_ASSET_PATH = 'assets/ui/purchaseSpinner/spine.spinner3';

/** The spine-animated spinner */
let spinner: SpineView | undefined;

/** The overlay covering the screen */
let overlay: View | undefined;

/**
 * Show the purchase loading, using the spine-animated spinner if the test is enabled
 * @returns
 */
export async function showLoadingAnimation() {
  // Show the default loading spinner anyway, that will also block the UI
  StateObserver.dispatch(showLoading());

  // Ensure the spinner is loaded. SPine must be loaded already before creating SpineView
  await spineLoader(SPINNER_ASSET_PATH);

  // Because the spinner is loaded async, we need to check if the loading is still showing
  if (!StateObserver.getState().ui.loading) return;

  const superview = getRootView();
  const zIndex = 10011; // One step above the default loading spinner

  // Create the overlay to cover the screen
  if (!overlay) {
    const screen = getScreenDimensions();
    // Screen height is different from root view height, so we have to calculate the offset
    const offsetY = (screen.height - superview.style.height) / 2;
    overlay = new View({
      ...uiConfig.popups.backgroundDarker,
      x: superview.style.width / 2,
      y: screen.height / 2 - offsetY,
      width: superview.style.width,
      height: screen.height,
      canHandleEvents: true,
    });
  }
  overlay.updateOpts({ superview, zIndex, opacity: 0 });
  animate(overlay).then({ opacity: 1 }, 200, animate.linear);

  // Create the animated spinner
  if (!spinner) {
    spinner = new SpineView({
      spinePath: SPINNER_ASSET_PATH,
      x: superview.style.width / 2,
      y: superview.style.height / 2,
    });
  }
  spinner.updateOpts({ superview, zIndex, scale: 0 });
  spinner.state.setAnimation(0, 'idle', true);
  animate(spinner).then({ scale: DOWNSCALE_FACTOR }, 300, animate.easeOutBack);
}

/**
 * Hide the purchase loading
 */
export function hideLoadingAnimation() {
  // Hide the default loading spinner
  StateObserver.dispatch(hideLoading());

  // Remove the overlay if it exists
  if (overlay) {
    overlay.removeFromSuperview();
  }

  // Remove the spinner if it exists
  if (spinner) {
    spinner.removeFromSuperview();
    spinner.state.clearTracks();
  }
}
